/**
 * The CSV's table — the machine file's header and cells (G2.4; ADR 9010 as amended by DEC-98 and
 * DEC-104, `docs/adr/9041`).
 *
 * The header is each column's machine key, its unit beside it; a money column is followed by its
 * own currency column; a provenance column (ADR 9120) wherever the builder put one. Every program
 * that reads an export reads this shape, so it does not move. Both CSV dialects (`docs/adr/9072`:
 * the raw file and the decimal-comma file for Excel set to Italian or Spanish) write this same
 * table; only the separator and the decimal mark differ.
 *
 * Until DEC-104 the Excel workbook was written from this table too. It no longer is: the workbook
 * is a working file for people (`xlsx.ts`) — translated headers, words for codes, one currency
 * said once, the provenance in a «Tecnico» sheet — laid out from the same request's columns and
 * rows, so the two files still carry the same rows and values.
 */

import type { ExportCell, ExportColumn } from './types';

/** One cell of the file: a data cell, or the currency cell a money column emits beside it. */
export type FileCell =
  | { readonly kind: 'cell'; readonly cell: ExportCell; readonly column: ExportColumn }
  | { readonly kind: 'currency'; readonly currency: string; readonly column: ExportColumn };

/**
 * Header row. A money column emits its own currency column alongside it, so
 * "explicit currency column" is satisfied by construction rather than by the
 * caller remembering.
 */
export function headerFields(columns: readonly ExportColumn[]): string[] {
  return columns.flatMap((c) => {
    const name = c.unit ? `${c.key} (${c.unit})` : c.key;
    return c.kind === 'money' ? [name, `${c.key}_currency`] : [name];
  });
}

/** A builder's row as the file's cells, in header order. */
export function fileRow(columns: readonly ExportColumn[], row: readonly ExportCell[]): FileCell[] {
  return columns.flatMap((column, i): FileCell[] => {
    const cell = row[i];
    if (cell === undefined) {
      throw new Error(`row is narrower than the column set: no cell for "${column.key}" at index ${i}`);
    }
    if (column.kind === 'money') {
      const currency = cell.kind === 'money' ? cell.currency : (column.currency ?? '');
      return [{ kind: 'cell', cell, column }, { kind: 'currency', currency, column }];
    }
    return [{ kind: 'cell', cell, column }];
  });
}
